/**
 * QA Console.
 *
 * Auth first: the console asks CAIT who you are, and shows the sign-in screen
 * rather than an error if you are not an admin. The earlier version simply
 * rendered "Authentication required" with no route forward, which made the whole
 * thing unusable.
 *
 * REAL ROUTES, not a `tab` variable in memory. The earlier version switched
 * screens with `useState<Tab>`, so the URL never changed and nothing was ever
 * pushed onto browser history — clicking around the console left no trace for
 * Back to undo, so pressing it skipped straight past every screen visited and
 * out of the app entirely. `console/api/main.ts`'s static file server already
 * falls back to `index.html` for any unknown path (see its own comment), so a
 * real path per screen — refreshable, bookmarkable, and Back/Forward-able —
 * was already supported server-side; nothing here was blocking it.
 */
import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type Me, type RunSummary } from './api';
import { SignIn } from './components/SignIn';
import { Shell } from './components/Shell';
import { NightlyBanner } from './components/NightlyBanner';
import { RunMatrix } from './components/RunMatrix';
import { RunLive } from './components/RunLive';
import { Reports } from './components/Reports';
import { TestsScreen } from './components/TestsScreen';
import { RecipesScreen } from './components/RecipesScreen';
import { Tier1Screen } from './components/Tier1Screen';
import { HealthScreen } from './components/HealthScreen';
import { TrackerScreen } from './components/TrackerScreen';
import { ExtrasScreen } from './components/ExtrasScreen';

type Auth =
  | { state: 'checking' }
  | { state: 'anonymous'; signInUrl: string }
  | { state: 'refused'; signInUrl: string; email: string; role: string }
  | { state: 'ready'; me: Me };

const PLAYER_FALLBACK = 'https://player.qa.teachingmachines.se/auth';

export default function App() {
  return (
    <BrowserRouter>
      <Console />
    </BrowserRouter>
  );
}

function Console() {
  const [auth, setAuth] = useState<Auth>({ state: 'checking' });
  const [history, setHistory] = useState<RunSummary[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.me()
      .then((me) => {
        if (!me.allowed) {
          setAuth({ state: 'refused', signInUrl: PLAYER_FALLBACK, email: me.user.email, role: me.user.role });
        } else {
          setAuth({ state: 'ready', me });
        }
      })
      .catch((e: ApiError & { signInUrl?: string }) => {
        setAuth({ state: 'anonymous', signInUrl: e.signInUrl ?? PLAYER_FALLBACK });
      });
  }, []);

  // History drives both the Reports tab and the header's live indicator, so it is
  // polled once here rather than in each screen.
  useEffect(() => {
    if (auth.state !== 'ready') return;
    const load = () => api.runs().then((r) => setHistory(r.runs)).catch(() => {});
    load();
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [auth.state]);

  const signOut = useCallback(async () => {
    await api.logout().catch(() => {});
    window.location.reload();
  }, []);

  if (auth.state === 'checking') {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <p className="text-sm" style={{ color: 'hsl(var(--text-faint))' }}>Checking your session…</p>
      </main>
    );
  }

  if (auth.state === 'anonymous') return <SignIn signInUrl={auth.signInUrl} />;

  if (auth.state === 'refused') {
    return <SignIn signInUrl={auth.signInUrl} refusal={{ email: auth.email, role: auth.role }} />;
  }

  const active = history.filter((r) => r.status === 'running' || r.status === 'queued');
  const reporterEmail = auth.me.user.email;

  return (
    <Shell user={auth.me.user} active={active} onSignOut={signOut}>
      <Routes>
        <Route path="/" element={<Navigate to="/runs" replace />} />

        <Route
          path="/runs"
          element={
            <div className="flex flex-col gap-5">
              <NightlyBanner history={history} />
              <RunMatrix onRunStarted={(run) => navigate(`/runs/${run.id}`)} />
            </div>
          }
        />
        <Route path="/runs/:runId" element={<RunLiveRoute />} />

        <Route
          path="/tests"
          element={
            <TestsScreen
              onOpenClientRecordings={() => navigate('/client-recordings')}
              onOpenRecipes={() => navigate('/recipes')}
            />
          }
        />
        <Route path="/recipes" element={<RecipesScreen />} />
        <Route path="/client-recordings" element={<Tier1Screen />} />
        {/* NOT `/health` — that exact path is the Docker healthcheck endpoint
            `server.ts` always serves, unconditionally, before anything else
            (see `main.ts`'s static-file fallback: it special-cases `/health`
            to never fall through to index.html). Colliding with it would mean
            this tab silently serves `{"status":"ok"}` instead of the Health
            screen on every direct visit or refresh. */}
        <Route path="/test-health" element={<HealthScreen />} />
        <Route path="/reports" element={<Reports onOpenRun={(id) => navigate(`/runs/${id}`)} reporterEmail={reporterEmail} />} />
        <Route path="/tracker" element={<TrackerScreen reporterEmail={reporterEmail} />} />
        <Route path="/extras" element={<ExtrasScreen />} />

        {/* An unknown path (a stale bookmark, a typo) lands somewhere real
            rather than a blank screen. */}
        <Route path="*" element={<Navigate to="/runs" replace />} />
      </Routes>
    </Shell>
  );
}

function RunLiveRoute() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  if (!runId) return <Navigate to="/runs" replace />;
  return <RunLive runId={runId} onClose={() => navigate('/runs')} />;
}
